/**
 * Information Center — team system notices (connection failures / operator actions).
 * Sibling of plan-level Clock attention; deep-links into Connections.
 */
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { SystemNotice } from '../services/devtoolsApi';
import { SectionCard } from './ui/SectionCard';

/** Accent CTA link states shared by panel rows and the critical TopBar strip. */
const NOTICE_CTA_CLASS =
  'font-medium text-[var(--accent)] underline hover:opacity-80 active:opacity-70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)]';

type Props = {
  notices?: SystemNotice[];
  loading?: boolean;
};

/** Home Information Center list for forge/Slack/Bugsnag operator actions. */
export function SystemNoticesPanel({ notices = [], loading }: Props) {
  const { t } = useTranslation();

  return (
    <SectionCard aria-label={t('systemNotices.title')} data-testid="system-notices-panel">
      <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-[var(--muted)]">
        {t('systemNotices.title')}
      </h2>
      {loading ? (
        <p className="text-sm text-[var(--muted)]">{t('common.loading')}</p>
      ) : notices.length === 0 ? (
        <p className="text-sm text-[var(--muted)]">{t('systemNotices.empty')}</p>
      ) : (
        <ul className="space-y-3 text-sm">
          {notices.map((n) => (
            <li key={n.noticeId} data-testid={`system-notice-${n.kind}`}>
              <p className="font-medium text-[var(--col-blocked)]">
                {t(`systemNotices.kinds.${n.kind}.title`, { defaultValue: n.title })}
              </p>
              <p className="mt-0.5 text-[var(--muted)]">
                {t(`systemNotices.kinds.${n.kind}.body`, { defaultValue: n.body })}
              </p>
              {n.href ? (
                <Link
                  to={n.href}
                  className={`mt-1 inline-block text-xs ${NOTICE_CTA_CLASS}`}
                >
                  {t(`systemNotices.kinds.${n.kind}.cta`, {
                    defaultValue: t('systemNotices.cta'),
                  })}
                </Link>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}

type StripProps = {
  notices?: SystemNotice[];
};

/** Critical strip under TopBar for the highest-severity system notice. */
export function SystemNoticesStrip({ notices = [] }: StripProps) {
  const { t } = useTranslation();
  // Only one strip: first critical notice wins so TopBar chrome stays calm.
  const critical = notices.find((n) => n.severity === 'critical');
  if (!critical) return null;
  return (
    <div
      role="status"
      data-testid="system-notices-strip"
      className="border-b border-[var(--col-blocked)] bg-[color-mix(in_srgb,var(--col-blocked)_12%,transparent)] px-4 py-2 text-sm md:px-6"
    >
      <span className="font-medium text-[var(--col-blocked)]">
        {t(`systemNotices.kinds.${critical.kind}.title`, { defaultValue: critical.title })}
      </span>
      <span className="mx-2 text-[var(--muted)]">—</span>
      <span className="text-[var(--muted)]">
        {t(`systemNotices.kinds.${critical.kind}.body`, { defaultValue: critical.body })}
      </span>
      {critical.href ? (
        <Link to={critical.href} className={`ml-3 ${NOTICE_CTA_CLASS}`}>
          {t(`systemNotices.kinds.${critical.kind}.cta`, {
            defaultValue: t('systemNotices.cta'),
          })}
        </Link>
      ) : null}
    </div>
  );
}
